import React, { useCallback, useMemo, useState } from "react";
import { apiUrl } from "@shared/utils/api";

/**
 * Accept-invite page: set initial password using token from email link.
 * Route: /accept-invite?token=...
 * Uses existing theme (card, #3A4A51 / #2B7F8D) to match login/forgot-password.
 */
export default function AcceptInvitePage({ brand: _brand }) {
  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("token") || "";
  }, []);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError(null);
      if (!token.trim()) {
        setError("Invite link is invalid or missing. Please use the link from your invite email.");
        return;
      }
      if (!newPassword.trim()) {
        setError("New password is required.");
        return;
      }
      if (newPassword !== confirmPassword) {
        setError("New password and confirmation do not match.");
        return;
      }
      setIsLoading(true);
      try {
        const res = await fetch(apiUrl("/api/v1/auth/accept_invite"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: token.trim(),
            new_password: newPassword,
            confirm_new_password: confirmPassword,
          }),
          credentials: "include",
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setError(data?.error || "Could not set password. Please try again.");
          setIsLoading(false);
          return;
        }
        setSuccess(true);
      } catch (err) {
        setError(err?.message || "Could not set password. Please try again.");
      }
      setIsLoading(false);
    },
    [token, newPassword, confirmPassword]
  );

  if (!token && !success) {
    return (
      <div
        className="w-full max-w-[560px] rounded-[18px] border border-black/5 bg-white p-6 sm:p-8 shadow-[0_18px_44px_rgba(0,0,0,0.14)]"
        role="main"
        aria-labelledby="accept-invite-title"
      >
        <h1 id="accept-invite-title" className="text-xl sm:text-2xl font-extrabold text-[#3A4A51] leading-tight">
          Set your password
        </h1>
        <p className="mt-4 text-sm text-slate-600">
          This page is for setting your password from an invite email. Please use the link from your invite email.
        </p>
      </div>
    );
  }

  if (success) {
    return (
      <div
        className="w-full max-w-[560px] rounded-[18px] border border-black/5 bg-white p-6 sm:p-8 shadow-[0_18px_44px_rgba(0,0,0,0.14)]"
        role="main"
        aria-labelledby="accept-invite-success-title"
      >
        <h1 id="accept-invite-success-title" className="text-xl sm:text-2xl font-extrabold text-[#3A4A51] leading-tight">
          Password set
        </h1>
        <p className="mt-4 text-sm text-slate-600">
          Your password has been set. You can now sign in with your email and this password.
        </p>
        <a
          href="/"
          className="mt-6 inline-block h-12 px-6 rounded-[4px] bg-[#2B7F8D] text-white font-bold hover:bg-[#256f7a] focus:ring-2 focus:ring-[#2B7F8D] focus:ring-offset-2 text-center leading-[3rem]"
        >
          Sign in
        </a>
      </div>
    );
  }

  return (
    <div
      className="w-full max-w-[560px] rounded-[18px] border border-black/5 bg-white p-6 sm:p-8 shadow-[0_18px_44px_rgba(0,0,0,0.14)]"
      role="main"
      aria-labelledby="accept-invite-title"
    >
      <h1 id="accept-invite-title" className="text-xl sm:text-2xl font-extrabold text-[#3A4A51] leading-tight">
        Set your password
      </h1>
      <p className="mt-2 text-sm text-slate-600">
        Choose a password to complete your account setup. You will use this to sign in.
      </p>
      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md animate-notice-slide-in" role="alert">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div>
          <label htmlFor="accept-invite-new-password" className="block text-sm font-extrabold tracking-wider text-[#2B7F8D]">
            NEW PASSWORD
          </label>
          <input
            id="accept-invite-new-password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="mt-2 h-12 w-full rounded-[4px] border-2 border-slate-300 px-3 sm:px-4 text-sm sm:text-base"
            placeholder="Enter new password"
            required
          />
        </div>
        <div>
          <label htmlFor="accept-invite-confirm-password" className="block text-sm font-extrabold tracking-wider text-[#2B7F8D]">
            CONFIRM PASSWORD
          </label>
          <input
            id="accept-invite-confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="mt-2 h-12 w-full rounded-[4px] border-2 border-slate-300 px-3 sm:px-4 text-sm sm:text-base"
            placeholder="Confirm new password"
            required
          />
        </div>
        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={isLoading || !newPassword.trim() || newPassword !== confirmPassword}
            className="flex-1 h-12 rounded-[4px] bg-[#2B7F8D] text-white font-bold hover:bg-[#256f7a] focus:ring-2 focus:ring-[#2B7F8D] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Setting…" : "Set password"}
          </button>
        </div>
      </form>
    </div>
  );
}
